"use client";

/**
 * GTO 教练 — 灯下道场的「追问层」
 *
 * 场景化:永远带着一手牌来追问。
 * - /train 错题卡「问教练」→ /coach?scene=X,顶部摆出那手牌(compact 牌桌 + 白话追问 chip);
 * - 无参数进入 → 自动摆出最近做错的一手;再没有才回退白话示例。
 * 清污:学习者不看工程旋钮(RAG/skill 固定走默认值),citations 不露素材出处。
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useSearchParams } from "next/navigation";
import { motion, useReducedMotion } from "motion/react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { getDueConcepts, getConceptStats } from "@/lib/fsrs";
import { loadProgress } from "@/lib/progress";
import { bbInline, conceptFacetZh, FACET_LABEL_ZH, positionZh, streetZh } from "@/lib/poker-terms-zh";
import { jargonZh, jargonZhProse } from "@/lib/jargon-zh";
import { MarkdownLite } from "@/components/markdown-lite";
import { PokerTable } from "@/components/poker-table";
import { DISPLAY_FONT, DOJO_EASE, EmptyGuide } from "@/components/dojo";
import type { PilotScene } from "@/lib/scene-types";
import {
  appendCoachHistory,
  clearCoachHistory,
  EMPTY_COACH_HISTORY,
  getCoachHistorySnapshot,
  subscribeCoachHistory,
} from "@/lib/coach-history";
import type { CoachCitation as Citation, CoachMessage as Message } from "@/lib/coach-history";

type CoachResp = {
  answer?: string;
  error?: string;
  concepts?: Record<string, string[]>;
  citations?: Citation[];
  detail?: string;
};

type SceneResponse = { scene?: PilotScene; error?: string };

/** 工程旋钮不暴露给学习者:能力保留,固定走后端默认值(rag=true / skill=auto) */
const COACH_RAG = true;
const COACH_SKILL = "auto";

/** 白话示例:面向 GTO 新手,不出现任何未中文化术语 */
const EXAMPLES = [
  "翻牌圈我该不该下注?",
  "对手加注,我要跟吗?",
  "什么时候该弃牌?",
  "手里是对子,该不该继续?",
];

/** 顺着一手牌的白话追问:三句通用 + 一句结合所在街 */
function buildFollowups(scene: PilotScene): string[] {
  return [
    "为什么不能跟注?",
    "他要是下得更大呢?",
    "换成同花听牌呢?",
    `${streetZh(scene.spot.street)}这里,到底该怎么打?`,
  ];
}

/** 从训练存档找最近一次答错的手牌 sceneId(无则 null) */
function latestWrongSceneId(): string | null {
  const { answered } = loadProgress();
  let best: { id: string; at: number } | null = null;
  for (const [id, entry] of Object.entries(answered)) {
    if (entry.last.correct) continue;
    if (!best || entry.last.at > best.at) best = { id, at: entry.last.at };
  }
  return best?.id ?? null;
}

function buildTrainingContext(): string {
  const parts: string[] = [];
  const stats = getConceptStats();
  if (stats.total > 0) {
    parts.push(`训练统计:已学 ${stats.total} 概念,待复习 ${stats.due},已掌握 ${stats.learned}。`);
  }
  const due = getDueConcepts(5);
  if (due.length > 0) {
    parts.push(`当前薄弱概念:${due.map((c) => c.key).join("、")}。`);
  }
  try {
    const raw = localStorage.getItem("gto-scene-progress-v2");
    if (raw) {
      const progress = JSON.parse(raw) as { answered: Record<string, { correct: boolean }> };
      const entries = Object.values(progress.answered);
      const total = entries.length;
      const correct = entries.filter((e) => e.correct).length;
      if (total > 0) {
        parts.push(`最近训练正确率:${Math.round((correct / total) * 100)}%(${correct}/${total})。`);
      }
    }
  } catch { /* ignore */ }
  return parts.join("\n");
}

// ─── 手牌上下文:带着牌来追问 ─────────────────────────────────────────────────

function HandContext({
  scene,
  source,
  collapsed,
  loading,
  onToggle,
  onAsk,
}: {
  scene: PilotScene;
  source: "param" | "wrong";
  collapsed: boolean;
  loading: boolean;
  onToggle: () => void;
  onAsk: (question: string) => void;
}) {
  const reduce = useReducedMotion();
  const chips = useMemo(() => buildFollowups(scene), [scene]);
  const { spot } = scene;
  const isParam = source === "param";

  return (
    <motion.section
      aria-label="追问的手牌"
      initial={reduce ? false : { opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0.2 } : { duration: 0.4, ease: DOJO_EASE }}
      className="mb-4 shrink-0 rounded-2xl border border-[#2A3D30] bg-[#0D1510]/90 p-4 shadow-[0_0_70px_-26px_rgba(228,184,91,0.3)]"
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "shrink-0 rounded-md px-2 py-0.5 text-[11px] font-bold",
            isParam
              ? "bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/30"
              : "bg-amber-300/15 text-amber-300 ring-1 ring-amber-300/30",
          )}
        >
          {isParam ? "你问的这手" : "最近做错的一手"}
        </span>
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-stone-100">
          {jargonZh(scene.title)}
        </h2>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "展开牌桌" : "收起牌桌"}
          className="shrink-0 rounded-md p-1 text-stone-500 transition hover:bg-[#14231A] hover:text-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
        >
          <ChevronDown
            className={cn("size-4 transition-transform duration-200", collapsed && "-rotate-90")}
          />
        </button>
      </div>
      <p className="mt-1 font-mono text-[11px] tabular-nums text-stone-500">
        {streetZh(spot.street)} · {positionZh(spot.hero_position)} vs{" "}
        {positionZh(spot.villain_position)}
        {spot.pot && ` · 底池 ${bbInline(spot.pot)}`}
      </p>

      {!collapsed && (
        <div className="mt-3">
          <PokerTable spot={scene.spot} compact />
          {spot.decision_point && (
            <p className="mt-2 text-xs leading-relaxed text-stone-400">
              {jargonZh(spot.decision_point)}
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#1E2E23] pt-3">
        <span className="text-[11px] font-medium text-stone-500">顺着这手牌追问:</span>
        {chips.map((c) => (
          <button
            key={c}
            type="button"
            disabled={loading}
            onClick={() => onAsk(c)}
            className="rounded-full border border-[#2A3D30] px-3 py-1 text-xs text-stone-300 transition hover:border-emerald-400/50 hover:bg-emerald-400/5 hover:text-emerald-200 active:scale-[0.97] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
          >
            {c}
          </button>
        ))}
      </div>
    </motion.section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function CoachPage() {
  const searchParams = useSearchParams();
  const sceneIdParam = searchParams.get("scene");
  const messages = useSyncExternalStore(
    subscribeCoachHistory,
    getCoachHistorySnapshot,
    () => EMPTY_COACH_HISTORY,
  );
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [contextScene, setContextScene] = useState<PilotScene | null>(null);
  const [contextSource, setContextSource] = useState<"param" | "wrong" | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 摆牌:?scene=X 优先(train 错题「问教练」deep-link);取不到或没带参数 → 最近做错的一手
  useEffect(() => {
    let cancelled = false;
    const tryIds: { id: string; source: "param" | "wrong" }[] = [];
    if (sceneIdParam) tryIds.push({ id: sceneIdParam, source: "param" });
    const wrongId = latestWrongSceneId();
    if (wrongId && wrongId !== sceneIdParam) tryIds.push({ id: wrongId, source: "wrong" });
    if (tryIds.length === 0) return;
    void (async () => {
      for (const t of tryIds) {
        try {
          const res = await fetch(`/api/scenes?id=${encodeURIComponent(t.id)}`, {
            cache: "no-store",
          });
          const json = (await res.json()) as SceneResponse;
          if (cancelled) return;
          if (json.scene) {
            setContextScene(json.scene);
            setContextSource(t.source);
            return;
          }
        } catch {
          /* 取不到就试下一个 */
        }
        if (cancelled) return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sceneIdParam]);

  const ask = useCallback(
    async (question: string) => {
      if (question.trim().length < 2 || loading) return;

      const userMsg: Message = { role: "user", text: question };
      appendCoachHistory(userMsg);
      setInput("");
      setLoading(true);
      setCollapsed(true); // 开问后牌桌收成一行摘要,把空间让给对话

      const context = buildTrainingContext();
      const fullQuestion = context
        ? `${question}\n\n[训练上下文]\n${context}`
        : question;

      try {
        const r = await fetch("/api/coach", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question: fullQuestion, rag: COACH_RAG, skill: COACH_SKILL }),
        });
        const data = (await r.json()) as CoachResp;
        const coachMsg: Message = {
          role: "coach",
          text: data.answer || data.error || data.detail || "无响应",
          citations: data.citations,
          concepts: data.concepts,
          error: data.error && !data.answer ? data.error : undefined,
        };
        appendCoachHistory(coachMsg);
      } catch (e) {
        appendCoachHistory({ role: "coach", text: String(e), error: String(e) });
      } finally {
        setLoading(false);
        setTimeout(() => {
          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
        }, 50);
      }
    },
    [loading],
  );

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] max-w-3xl flex-col">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.24em] text-amber-300/70">
            GTO DOJO · 教练
          </p>
          <h1
            className={cn(
              DISPLAY_FONT,
              "mt-1 text-2xl font-extrabold tracking-tight text-stone-50",
            )}
          >
            GTO 教练
          </h1>
          <p className="mt-1 text-sm text-stone-500">牌桌上没想明白的,带着那手牌来追问</p>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clearCoachHistory}
            className="shrink-0 rounded-md border border-[#223329] px-2.5 py-1.5 text-xs text-stone-500 transition hover:border-rose-400/40 hover:text-rose-300"
          >
            清空对话
          </button>
        )}
      </header>

      {/* 手牌上下文:教练永远带着一手牌 */}
      {contextScene && contextSource && (
        <HandContext
          scene={contextScene}
          source={contextSource}
          collapsed={collapsed}
          loading={loading}
          onToggle={() => setCollapsed((c) => !c)}
          onAsk={(q) => void ask(q)}
        />
      )}

      {/* Chat Area */}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        {messages.length === 0 && !contextScene && (
          <div className="space-y-4">
            {/* PLAN #17f5 ②:零输入空态 → ≥3 张入口卡(新手第一课/牌面题库/场景演练) */}
            <EmptyGuide
              title="想到什么就问什么 — 也可以先从这三扇门进去"
              subtitle="教练会结合你的练习情况,用大白话讲给你听;零基础就从第一课开始。"
              cards={[
                {
                  href: "/lessons",
                  title: "新手第一课",
                  desc: "零基础入门课,看完就知道牌桌上该先想什么。",
                  cta: "去学",
                },
                {
                  href: "/quiz",
                  title: "牌面题库",
                  desc: "做几道牌面题,做错的直接带来问教练。",
                  cta: "去做题",
                },
                {
                  href: "/scenes",
                  title: "场景演练",
                  desc: "真实牌桌场景逐个拆解,看中哪手就练哪手。",
                  cta: "去演练",
                },
              ]}
            />
            <div className="text-center">
              <p className="text-xs text-stone-600">或者直接白问一句:</p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => void ask(ex)}
                    className="rounded-full border border-[#223329] px-3 py-1.5 text-xs text-stone-400 transition hover:border-emerald-400/50 hover:text-emerald-200"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.length === 0 && contextScene && (
          <p className="text-center text-xs text-stone-600">
            点上面的追问直接开问,或在下面打字。
          </p>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={cn(
              "flex",
              msg.role === "user" ? "justify-end" : "justify-start",
            )}
          >
            <div
              className={cn(
                "max-w-[85%] rounded-2xl px-4 py-3",
                msg.role === "user"
                  ? "bg-emerald-400/15 text-emerald-50 ring-1 ring-inset ring-emerald-400/20"
                  : msg.error
                    ? "border border-rose-400/30 bg-rose-400/10 text-rose-200"
                    : "border border-[#223329] bg-[#0D1510]/85 text-stone-100",
              )}
            >
              {/* r2 契约 item 4:教练回复为 LLM markdown,经 MarkdownLite 渲染,
                  裸露 ** / ## 记号不入学习者屏(机检:probe/learner-dom-surface/);
                  r3 learner_surface_purity:答文体裸英文术语先过 jargonZhProse 显示层翻译
                  (存储数据零改动,未登录词静默原样) */}
              <MarkdownLite text={jargonZhProse(msg.text)} className="text-sm leading-relaxed" />

              {msg.concepts && Object.keys(msg.concepts).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {Object.entries(msg.concepts).flatMap(([k, vals]) =>
                    (vals || []).slice(0, 4).map((v) => (
                      <span key={`${k}-${v}`} className="rounded bg-[#1E2E23] px-1.5 py-0.5 text-[10px] text-stone-500">
                        {FACET_LABEL_ZH[k]
                          ? `${FACET_LABEL_ZH[k]} ${conceptFacetZh(k, v)}`
                          : conceptFacetZh(k, v)}
                      </span>
                    )),
                  )}
                </div>
              )}

              {(msg.citations || []).length > 0 && (
                <p className="mt-2 border-t border-[#1E2E23] pt-2 text-[11px] text-stone-500">
                  参考了 {(msg.citations || []).length} 条本地讲解
                </p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl border border-[#223329] bg-[#0D1510]/85 px-4 py-3">
              <span className="flex items-center gap-1.5 text-sm text-stone-500">
                <span className="inline-block size-1.5 animate-pulse rounded-full bg-emerald-400" />
                <span className="inline-block size-1.5 animate-pulse rounded-full bg-emerald-400 [animation-delay:150ms]" />
                <span className="inline-block size-1.5 animate-pulse rounded-full bg-emerald-400 [animation-delay:300ms]" />
                <span className="ml-2">思考中…</span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="mt-4 space-y-2 rounded-xl border border-[#223329] bg-[#0D1510]/80 p-3">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void ask(input);
              }
            }}
            placeholder={
              contextScene
                ? "顺着这手牌,想问什么?…(Enter 发送)"
                : "用大白话描述你的牌局问题…(Enter 发送)"
            }
            className="flex-1 rounded-lg border border-[#2A3D30] bg-[#0C130F] px-3 py-2 text-sm text-stone-100 outline-none transition-colors focus:border-emerald-400/60"
          />
          <button
            type="button"
            disabled={loading || input.trim().length < 2}
            onClick={() => void ask(input)}
            className="rounded-lg bg-emerald-400 px-4 py-2 text-sm font-bold text-[#062015] transition hover:bg-emerald-300 active:scale-[0.98] disabled:opacity-40"
          >
            发送
          </button>
        </div>
        <p className="text-[11px] text-stone-600">教练会结合你的练习情况回答 · 全程大白话</p>
      </div>
    </div>
  );
}
